import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  Brain, TrendingUp, Target, BarChart3, AlertTriangle, CheckCircle,
  ArrowUp, ArrowDown, Minus, DollarSign, Shield, Users, Calendar,
  Lightbulb, FileText, Activity, PieChart, Zap, Award, Building,
  AlertCircle, ChevronRight, Plus, Search, RefreshCw, Download,
  Clock, HelpCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Risk Level Colors
const getRiskLevelColor = (level) => {
  const colors = {
    very_low: 'bg-green-100 text-green-800 border-green-200',
    low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    moderate: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    very_high: 'bg-red-100 text-red-800 border-red-200',
    critical: 'bg-red-200 text-red-900 border-red-300'
  };
  return colors[level] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const getRiskLevelBg = (level) => {
  const colors = {
    very_low: 'from-green-500 to-green-600',
    low: 'from-emerald-500 to-emerald-600',
    moderate: 'from-yellow-500 to-yellow-600',
    high: 'from-orange-500 to-orange-600',
    very_high: 'from-red-500 to-red-600',
    critical: 'from-red-600 to-red-700'
  };
  return colors[level] || 'from-gray-500 to-gray-600';
};

const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <ArrowDown className="w-4 h-4 text-green-500" />;
  if (trend === 'declining') return <ArrowUp className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-500" />;
};

const AnalyticsIntelligenceDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('predictive');
  
  // Predictive Risk State
  const [riskScore, setRiskScore] = useState(null);
  const [riskTrends, setRiskTrends] = useState([]);
  const [riskCategories, setRiskCategories] = useState([]);
  
  // Benchmarking State
  const [benchmarkReport, setBenchmarkReport] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('23');
  const [peerComparison, setPeerComparison] = useState(null);
  
  // RCA State
  const [rcaList, setRcaList] = useState([]);
  const [rcaTemplates, setRcaTemplates] = useState(null);
  const [showCreateRCA, setShowCreateRCA] = useState(false);
  const [selectedRCA, setSelectedRCA] = useState(null);
  const [newRCA, setNewRCA] = useState({
    incident_description: '',
    incident_date: new Date().toISOString().split('T')[0],
    analysis_method: 'five_why',
    analyst_name: ''
  });
  
  // ROI State
  const [roiDashboard, setRoiDashboard] = useState(null);
  const [roiMetrics, setRoiMetrics] = useState([]);
  const [roiTrends, setRoiTrends] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('ytd');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Load initial data
  useEffect(() => {
    loadPredictiveRisk();
    loadIndustries();
    loadRCAList();
    loadRCATemplates();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'benchmarking' && !benchmarkReport) {
      loadBenchmarkReport();
    } else if (activeTab === 'roi' && !roiDashboard) {
      loadROIDashboard();
    }
  }, [activeTab]);

  // ============ PREDICTIVE RISK FUNCTIONS ============
  
  const loadPredictiveRisk = async () => {
    setLoading(true);
    try {
      const [scoreRes, trendsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/analytics/predictive-risk/score`),
        axios.get(`${API}/analytics/predictive-risk/trends?months=6`),
        axios.get(`${API}/analytics/predictive-risk/categories`)
      ]);
      setRiskScore(scoreRes.data);
      setRiskTrends(trendsRes.data.trends);
      setRiskCategories(categoriesRes.data.categories);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load predictive risk data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // ============ BENCHMARKING FUNCTIONS ============
  
  const loadIndustries = async () => {
    try {
      const response = await axios.get(`${API}/analytics/benchmarking/industries`);
      setIndustries(response.data.industries);
    } catch (error) {
      console.error('Failed to load industries');
    }
  };

  const loadBenchmarkReport = async () => {
    setLoading(true);
    try {
      const [reportRes, peerRes] = await Promise.all([
        axios.get(`${API}/analytics/benchmarking/report?industry_code=${selectedIndustry}`),
        axios.get(`${API}/analytics/benchmarking/comparison?metric=trir`)
      ]);
      setBenchmarkReport(reportRes.data);
      setPeerComparison(peerRes.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load benchmarking data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // ============ RCA FUNCTIONS ============
  
  const loadRCAList = async () => {
    try {
      const response = await axios.get(`${API}/analytics/rca/list`);
      setRcaList(response.data.analyses);
    } catch (error) {
      console.error('Failed to load RCA list');
    }
  };

  const loadRCATemplates = async () => {
    try {
      const response = await axios.get(`${API}/analytics/rca/templates`);
      setRcaTemplates(response.data);
    } catch (error) {
      console.error('Failed to load RCA templates');
    }
  };

  const createRCA = async () => {
    if (!newRCA.incident_description || !newRCA.analyst_name) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/analytics/rca/create`, null, {
        params: {
          incident_description: newRCA.incident_description,
          incident_date: new Date(newRCA.incident_date).toISOString(),
          analysis_method: newRCA.analysis_method,
          analyst_id: 'current_user',
          analyst_name: newRCA.analyst_name
        }
      });
      
      toast({
        title: "RCA Created",
        description: `Root Cause Analysis created using ${newRCA.analysis_method.replace('_', ' ')} method`
      });
      
      setShowCreateRCA(false);
      setNewRCA({
        incident_description: '',
        incident_date: new Date().toISOString().split('T')[0],
        analysis_method: 'five_why',
        analyst_name: ''
      });
      loadRCAList();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create RCA",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // ============ ROI FUNCTIONS ============
  
  const loadROIDashboard = async () => {
    setLoading(true);
    try {
      const [dashboardRes, metricsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/analytics/roi/dashboard?period=${selectedPeriod}`),
        axios.get(`${API}/analytics/roi/metrics`),
        axios.get(`${API}/analytics/roi/trends?months=12`)
      ]);
      setRoiDashboard(dashboardRes.data);
      setRoiMetrics(metricsRes.data.metrics);
      setRoiTrends(trendsRes.data.trends);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load ROI data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Universal Page Header */}
      <PageHeader
        title="Analytics & Intelligence"
        subtitle="Predictive Risk Scoring • Industry Benchmarking • Root Cause Analysis • Safety ROI"
        icon={Brain}
        variant="purple"
        onRefresh={() => {
          if (activeTab === 'predictive') loadPredictiveRisk();
          else if (activeTab === 'benchmarking') loadBenchmarkReport();
          else if (activeTab === 'roi') loadROIDashboard();
          else loadRCAList();
        }}
        loading={loading}
      />

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto p-1.5 gap-1 bg-gray-100 rounded-xl">
          <TabsTrigger 
            value="predictive"
            className="flex items-center gap-2 py-3 px-3 rounded-lg transition-all data-[state=active]:bg-purple-600 data-[state=active]:text-white"
          >
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">Predictive Risk</span>
            <span className="sm:hidden">Risk</span>
          </TabsTrigger>
          <TabsTrigger 
            value="benchmarking"
            className="flex items-center gap-2 py-3 px-3 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white"
          >
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Benchmarking</span>
            <span className="sm:hidden">Bench</span>
          </TabsTrigger>
          <TabsTrigger 
            value="rca"
            className="flex items-center gap-2 py-3 px-3 rounded-lg transition-all data-[state=active]:bg-orange-600 data-[state=active]:text-white"
          >
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Root Cause</span>
            <span className="sm:hidden">RCA</span>
          </TabsTrigger>
          <TabsTrigger 
            value="roi"
            className="flex items-center gap-2 py-3 px-3 rounded-lg transition-all data-[state=active]:bg-green-600 data-[state=active]:text-white"
          >
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">Safety ROI</span>
            <span className="sm:hidden">ROI</span>
          </TabsTrigger>
        </TabsList>

        {/* ============ PREDICTIVE RISK TAB ============ */}
        <TabsContent value="predictive" className="space-y-6">
          {riskScore && (
            <>
              {/* Risk Score Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Risk Score Card */}
                <Card className={`lg:col-span-1 bg-gradient-to-br ${getRiskLevelBg(riskScore.risk_level)} text-white`}>
                  <CardHeader>
                    <CardTitle className="text-lg text-white/90">Overall Risk Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-6xl font-bold mb-2">
                        {Math.round(riskScore.overall_score)}
                      </div>
                      <Badge className="bg-white/20 text-white text-lg px-4 py-1">
                        {riskScore.risk_level.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <div className="mt-4 text-sm text-white/80">
                        <p>Confidence: {Math.round(riskScore.confidence_score * 100)}%</p>
                        <p className="mt-1">7-Day Prediction Horizon</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Risk Factors */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Contributing Risk Factors
                    </CardTitle>
                    <CardDescription>Factors influencing your current risk score</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {riskScore.risk_factors?.map((factor, index) => (
                        <div key={index} className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{factor.factor_name}</span>
                            <span className={`font-semibold ${factor.contribution_score > 50 ? 'text-red-600' : factor.contribution_score > 30 ? 'text-orange-600' : 'text-green-600'}`}>
                              {Math.round(factor.contribution_score)}%
                            </span>
                          </div>
                          <Progress 
                            value={factor.contribution_score} 
                            className="h-2"
                          />
                          <p className="text-xs text-gray-500">{factor.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recommendations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    AI-Powered Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {riskScore.recommendations?.map((rec, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-semibold">{index + 1}</span>
                        </div>
                        <p className="text-sm text-gray-700">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Risk by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-purple-500" />
                    Risk by Incident Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {riskCategories.slice(0, 10).map((cat, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg text-center">
                        <div className={`text-2xl font-bold ${cat.risk_score > 60 ? 'text-red-600' : cat.risk_score > 40 ? 'text-orange-600' : 'text-green-600'}`}>
                          {Math.round(cat.risk_score)}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 truncate" title={cat.display_name}>
                          {cat.display_name}
                        </p>
                        <div className="flex items-center justify-center mt-2 text-xs">
                          <TrendIcon trend={cat.trend} />
                          <span className="ml-1 capitalize">{cat.trend}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Historical Trends */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Historical Risk Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Period</th>
                          <th className="text-center py-2 px-3">Risk Score</th>
                          <th className="text-center py-2 px-3">Incidents</th>
                          <th className="text-center py-2 px-3">Near Misses</th>
                          <th className="text-center py-2 px-3">TRIR</th>
                          <th className="text-center py-2 px-3">DART</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskTrends.map((trend, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{trend.period}</td>
                            <td className="text-center py-2 px-3">
                              <Badge className={getRiskLevelColor(trend.risk_score > 50 ? 'high' : 'low')}>
                                {Math.round(trend.risk_score)}
                              </Badge>
                            </td>
                            <td className="text-center py-2 px-3">{trend.incidents}</td>
                            <td className="text-center py-2 px-3">{trend.near_misses}</td>
                            <td className="text-center py-2 px-3">{trend.trir?.toFixed(2)}</td>
                            <td className="text-center py-2 px-3">{trend.dart?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ============ BENCHMARKING TAB ============ */}
        <TabsContent value="benchmarking" className="space-y-6">
          {/* Industry Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-500" />
                Industry Benchmarking
              </CardTitle>
              <CardDescription>Compare your safety performance against industry standards</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label>Select Industry</Label>
                  <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind.code} value={ind.code}>
                          {ind.name} (NAICS {ind.naics})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadBenchmarkReport} disabled={loading}>
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Generate Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {benchmarkReport && (
            <>
              {/* Overall Ranking */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className={`${benchmarkReport.overall_ranking === 'above_average' ? 'bg-green-50 border-green-200' : benchmarkReport.overall_ranking === 'below_average' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <CardContent className="p-6 text-center">
                    <Award className={`w-12 h-12 mx-auto mb-3 ${benchmarkReport.overall_ranking === 'above_average' ? 'text-green-600' : benchmarkReport.overall_ranking === 'below_average' ? 'text-red-600' : 'text-yellow-600'}`} />
                    <h3 className="text-lg font-semibold text-gray-900">Overall Ranking</h3>
                    <p className="text-2xl font-bold mt-2 capitalize">
                      {benchmarkReport.overall_ranking.replace('_', ' ')}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">{benchmarkReport.industry_name}</p>
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Strengths
                    </h3>
                    <ul className="mt-3 space-y-2">
                      {benchmarkReport.strengths?.map((s, i) => (
                        <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                          <span className="text-blue-500">✓</span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Areas for Improvement
                    </h3>
                    <ul className="mt-3 space-y-2">
                      {benchmarkReport.improvement_areas?.map((a, i) => (
                        <li key={i} className="text-sm text-orange-800 flex items-start gap-2">
                          <span className="text-orange-500">→</span>
                          {a}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Metrics Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>Safety Metrics vs. Industry</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {benchmarkReport.metrics?.map((metric, index) => (
                      <div key={index} className="border-b pb-4 last:border-0">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                          <span className="font-medium">{metric.metric_name}</span>
                          <div className="flex items-center gap-2">
                            <TrendIcon trend={metric.trend} />
                            <span className="text-sm capitalize text-gray-500">{metric.trend}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-center text-sm">
                          <div className="bg-blue-50 p-2 rounded">
                            <p className="text-gray-500">Your Value</p>
                            <p className="font-bold text-blue-600">{metric.your_value} {metric.unit}</p>
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <p className="text-gray-500">Industry Avg</p>
                            <p className="font-semibold">{metric.industry_average} {metric.unit}</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <p className="text-gray-500">Best in Class</p>
                            <p className="font-semibold text-green-600">{metric.industry_best} {metric.unit}</p>
                          </div>
                          <div className="bg-red-50 p-2 rounded">
                            <p className="text-gray-500">Industry Worst</p>
                            <p className="font-semibold text-red-600">{metric.industry_worst} {metric.unit}</p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Best</span>
                            <span>Percentile: {metric.percentile}%</span>
                            <span>Worst</span>
                          </div>
                          <div className="relative h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded">
                            <div 
                              className="absolute w-3 h-3 bg-blue-600 rounded-full border-2 border-white shadow-md -top-0.5"
                              style={{ left: `calc(${metric.percentile}% - 6px)` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Peer Comparison */}
              {peerComparison && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-500" />
                      Peer Comparison - {peerComparison.metric_name}
                    </CardTitle>
                    <CardDescription>
                      You rank #{peerComparison.your_rank} out of {peerComparison.total_peers} peers 
                      (better than {peerComparison.better_than_percent}%)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {peerComparison.peers?.map((peer, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center justify-between p-3 rounded-lg ${peer.is_you ? 'bg-blue-100 border-2 border-blue-400' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full ${peer.rank <= 3 ? 'bg-yellow-400 text-yellow-900' : 'bg-gray-200'} font-bold text-sm`}>
                              {peer.rank}
                            </span>
                            <span className={`font-medium ${peer.is_you ? 'text-blue-700' : ''}`}>
                              {peer.company}
                              {peer.is_you && <Badge className="ml-2 bg-blue-600">You</Badge>}
                            </span>
                          </div>
                          <span className="font-semibold">{peer.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ============ ROOT CAUSE ANALYSIS TAB ============ */}
        <TabsContent value="rca" className="space-y-6">
          {/* Create RCA Button */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Root Cause Analyses</h2>
              <p className="text-gray-600 text-sm">Investigate incidents and identify root causes</p>
            </div>
            <Button onClick={() => setShowCreateRCA(true)} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              New Analysis
            </Button>
          </div>

          {/* Create RCA Modal */}
          {showCreateRCA && (
            <Card className="border-2 border-orange-200">
              <CardHeader>
                <CardTitle>Create New Root Cause Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Incident Description *</Label>
                  <Textarea
                    value={newRCA.incident_description}
                    onChange={(e) => setNewRCA({...newRCA, incident_description: e.target.value})}
                    placeholder="Describe the incident in detail..."
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Incident Date *</Label>
                    <Input
                      type="date"
                      value={newRCA.incident_date}
                      onChange={(e) => setNewRCA({...newRCA, incident_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Analysis Method</Label>
                    <Select 
                      value={newRCA.analysis_method} 
                      onValueChange={(v) => setNewRCA({...newRCA, analysis_method: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="five_why">5-Why Analysis</SelectItem>
                        <SelectItem value="fishbone">Fishbone Diagram</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Analyst Name *</Label>
                    <Input
                      value={newRCA.analyst_name}
                      onChange={(e) => setNewRCA({...newRCA, analyst_name: e.target.value})}
                      placeholder="Your name"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={createRCA} disabled={loading}>
                    {loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                    Create Analysis
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateRCA(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* RCA Templates */}
          {rcaTemplates && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {rcaTemplates.methods?.map((method, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {method.method === 'five_why' ? (
                        <HelpCircle className="w-5 h-5 text-blue-500" />
                      ) : (
                        <Target className="w-5 h-5 text-purple-500" />
                      )}
                      {method.name}
                    </CardTitle>
                    <CardDescription>{method.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Best for:</strong> {method.best_for}
                    </p>
                    {method.steps ? (
                      <ol className="list-decimal list-inside text-sm space-y-1 text-gray-600">
                        {method.steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(method.categories || {}).map(([cat, desc]) => (
                          <div key={cat} className="text-sm">
                            <span className="font-medium text-gray-700">{cat}:</span>
                            <span className="text-gray-600 ml-2">{desc}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* RCA List */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Analyses</CardTitle>
            </CardHeader>
            <CardContent>
              {rcaList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No root cause analyses yet.</p>
                  <p className="text-sm">Create your first analysis to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {rcaList.map((rca, index) => (
                    <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{rca.incident_description?.substring(0, 100)}...</h4>
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(rca.incident_date).toLocaleDateString()}
                            </span>
                            <Badge variant="outline">{rca.analysis_method?.replace('_', ' ')}</Badge>
                            <Badge className={rca.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {rca.status}
                            </Badge>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Common Root Causes Reference */}
          {rcaTemplates && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-500" />
                  Common Root Causes Reference
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {rcaTemplates.common_root_causes?.map((cause, index) => (
                    <Badge key={index} variant="outline" className="text-sm py-1 px-3">
                      {cause}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ============ SAFETY ROI TAB ============ */}
        <TabsContent value="roi" className="space-y-6">
          {/* Period Selector */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label>Reporting Period</Label>
                  <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="q1">Q1</SelectItem>
                      <SelectItem value="q2">Q2</SelectItem>
                      <SelectItem value="q3">Q3</SelectItem>
                      <SelectItem value="q4">Q4</SelectItem>
                      <SelectItem value="fy">Full Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadROIDashboard} disabled={loading}>
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Update Report
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {roiDashboard && (
            <>
              {/* ROI Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="p-6">
                    <DollarSign className="w-8 h-8 mb-2 text-green-100" />
                    <p className="text-green-100 text-sm">ROI Percentage</p>
                    <p className="text-3xl font-bold">{roiDashboard.roi_percentage?.toFixed(1)}%</p>
                    <p className="text-green-100 text-xs mt-1">Return on safety investment</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-6">
                    <TrendingUp className="w-8 h-8 mb-2 text-blue-100" />
                    <p className="text-blue-100 text-sm">Total Investment</p>
                    <p className="text-3xl font-bold">{formatCurrency(roiDashboard.total_safety_investment)}</p>
                    <p className="text-blue-100 text-xs mt-1">{roiDashboard.report_period}</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="p-6">
                    <Zap className="w-8 h-8 mb-2 text-purple-100" />
                    <p className="text-purple-100 text-sm">Cost Savings</p>
                    <p className="text-3xl font-bold">{formatCurrency(roiDashboard.total_cost_savings)}</p>
                    <p className="text-purple-100 text-xs mt-1">Estimated total savings</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                  <CardContent className="p-6">
                    <Clock className="w-8 h-8 mb-2 text-orange-100" />
                    <p className="text-orange-100 text-sm">Payback Period</p>
                    <p className="text-3xl font-bold">{roiDashboard.payback_period_months?.toFixed(1)}</p>
                    <p className="text-orange-100 text-xs mt-1">Months</p>
                  </CardContent>
                </Card>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6 text-center">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="text-gray-600 text-sm">Incidents Prevented</p>
                    <p className="text-4xl font-bold text-green-600">{roiDashboard.incidents_prevented_estimate}</p>
                    <p className="text-xs text-gray-500 mt-1">Estimated based on industry rates</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                    <p className="text-gray-600 text-sm">Days Without Incident</p>
                    <p className="text-4xl font-bold text-blue-600">{roiDashboard.days_without_incident}</p>
                    <p className="text-xs text-gray-500 mt-1">In reporting period</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 text-center">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-purple-500" />
                    <p className="text-gray-600 text-sm">Workers&apos; Comp Savings</p>
                    <p className="text-4xl font-bold text-purple-600">{formatCurrency(roiDashboard.workers_comp_savings)}</p>
                    <p className="text-xs text-gray-500 mt-1">Reduced claims</p>
                  </CardContent>
                </Card>
              </div>

              {/* Investment & Savings Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                      Investment Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(roiDashboard.investment_breakdown || {}).map(([category, amount]) => (
                        <div key={category}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{category}</span>
                            <span className="font-medium">{formatCurrency(amount)}</span>
                          </div>
                          <Progress 
                            value={(amount / roiDashboard.total_safety_investment) * 100} 
                            className="h-2"
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      Cost Savings Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {roiDashboard.cost_savings_breakdown?.map((saving, index) => (
                        <div key={index} className="border-b pb-3 last:border-0">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{saving.category}</p>
                              <p className="text-xs text-gray-500">{saving.description}</p>
                            </div>
                            <span className="font-bold text-green-600">{formatCurrency(saving.amount)}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{saving.calculation_basis}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* ROI Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Key ROI Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {roiMetrics.map((metric, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">{metric.metric_name}</p>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="text-2xl font-bold">
                            {metric.unit === 'USD' ? formatCurrency(metric.value) : `${metric.value.toFixed(1)}${metric.unit === '%' ? '%' : ''}`}
                          </span>
                          <TrendIcon trend={metric.trend} />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{metric.comparison_period}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsIntelligenceDashboard;
